import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, MapPin, DollarSign, Phone, Layers, Link as LinkIcon, UploadCloud, Loader2, X, CheckSquare } from 'lucide-react';
import { useCreateFranchise, useUpdateFranchise, useFranchise } from '@/hooks/useFranchise';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { NumberInput } from '@/components/ui/numberInput';
import AddressSelector from '@/components/common/AddressSelector';
import BusinessSelector from '@/components/common/BusinessSelector';
import LocationPicker from '@/components/rental/LocationPicker';
import { useProvinces, useDistricts, useWards } from '@/hooks/useAddress';
import { cn } from '@/lib/utils';
import { imageApi } from '@/api/imageApi';
import { useUIStore } from '@/store/uiStore';
import { FileUploadDropzone } from '@/components/common';
import { Checkbox, FormControlLabel } from '@mui/material';

const AREA_UNITS = [
  { value: 'm²', label: 'm²' },
  { value: 'ft²', label: 'ft²' },
];
const CURRENCIES = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
  { value: 'JPY', label: 'JPY' },
];
const BUSINESS_SECTORS = [
  { value: 'F&B', label: 'F&B' },
  { value: 'Retail', label: 'Bán lẻ' },
  { value: 'Education', label: 'Giáo dục' },
  { value: 'Service', label: 'Dịch vụ' },
];
const BUSINESS_TYPES = [
  { value: 'Cửa hàng ăn uống', label: 'Cửa hàng ăn uống' },
  { value: 'Quán cà phê / Trà sữa', label: 'Quán cà phê / Trà sữa' },
  { value: 'Thức ăn nhanh', label: 'Thức ăn nhanh' },
  { value: 'Nhà hàng', label: 'Nhà hàng' },
  { value: 'Khác', label: 'Khác' },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1950 + 1 }, (_, i) => {
  const y = (CURRENT_YEAR - i).toString();
  return { value: y, label: y };
});

const optionalNumber = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  },
  z.number().optional()
);

const schema = z.object({
  name: z.string().min(3, 'Tối thiểu 3 ký tự'),
  description: z.string().min(20, 'Tối thiểu 20 ký tự').optional().or(z.literal('')),
  logo: z.string().url('URL không hợp lệ').optional().or(z.literal('')),
  countryId: z.coerce.number().min(1, 'Chọn quốc gia'),
  provinceId: z.coerce.number().min(1, 'Chọn tỉnh/thành'),
  districtId: z.coerce.number().min(1, 'Chọn quận/huyện'),
  wardId: z.coerce.number().min(1, 'Chọn phường/xã'),
  headquarters: z.string().min(5, 'Nhập địa chỉ cụ thể'),
  areaUnit: z.string().default('m²'),
  currencyUnit: z.string().default('VND'),
  website: z.string().url('URL không hợp lệ').optional().or(z.literal('')),
  contactEmail: z.string().email('Email không hợp lệ'),
  contactPhone: z.string().min(9, 'Nhập số điện thoại'),
  totalLocations: optionalNumber,
  establishedYear: optionalNumber,
  investmentRequiredMin: optionalNumber,
  investmentRequiredMax: optionalNumber,
  royaltyFee: optionalNumber,
  franchiseFee: optionalNumber,
  advertisingFee: optionalNumber,
  contractDuration: optionalNumber,
  trainingProgram: z.boolean().default(false),
  supportProvided: z.string().optional(),
  requirements: z.string().optional(),
  targetMarket: z.string().optional(),
  averageRevenue: optionalNumber,
  profitMargin: optionalNumber,
  businessSectorId: z.string().optional(),
  businessSectorName: z.string().optional(),
  businessTypeId: z.string().optional(),
  businessTypeName: z.string().optional(),
  businessType: z.string().optional(),
  businessSector: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

export default function CreateFranchisePage() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { addToast } = useUIStore();
  const { mutate: create, isPending: isCreating } = useCreateFranchise();
  const { mutate: update, isPending: isUpdating } = useUpdateFranchise();
  const isPending = isCreating || isUpdating;

  const { data: rawData } = useFranchise(id || '');
  const existingData = (rawData as any)?.data ?? rawData;

  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedGeo, setSelectedGeo] = useState<{ lat: string, lon: string, display_name: string } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      countryId: 1, // VN mặc định
      currencyUnit: 'VND',
      areaUnit: 'm²',
      trainingProgram: false,
    },
  });

  useEffect(() => {
    if (isEditMode && existingData) {
      reset({
        name: existingData.name || '',
        description: existingData.description || '',
        countryId: existingData.countryId || 1,
        provinceId: existingData.provinceId || 1,
        districtId: existingData.districtId || 1,
        wardId: existingData.wardId || 1,
        headquarters: existingData.headquarters || '',
        areaUnit: existingData.areaUnit || 'm²',
        currencyUnit: existingData.currencyUnit || 'VND',
        website: existingData.website || '',
        contactEmail: existingData.contact_email || existingData.contactEmail || '',
        contactPhone: existingData.contact_phone || existingData.contactPhone || '',
        totalLocations: existingData.total_locations ?? existingData.totalLocations ?? undefined,
        establishedYear: existingData.established_year ?? existingData.establishedYear ?? undefined,
        investmentRequiredMin: existingData.investment_required_min != null ? Number(existingData.investment_required_min) : undefined,
        investmentRequiredMax: existingData.investment_required_max != null ? Number(existingData.investment_required_max) : undefined,
        royaltyFee: existingData.royalty_fee != null ? Number(existingData.royalty_fee) : undefined,
        franchiseFee: existingData.franchise_fee != null ? Number(existingData.franchise_fee) : undefined,
        advertisingFee: existingData.advertising_fee != null ? Number(existingData.advertising_fee) : undefined,
        contractDuration: existingData.contract_duration ?? undefined,
        trainingProgram: existingData.training_program ?? existingData.trainingProgram ?? false,
        supportProvided: existingData.support_provided || existingData.supportProvided || '',
        requirements: existingData.requirements || '',
        targetMarket: existingData.target_market || existingData.targetMarket || '',
        averageRevenue: existingData.average_revenue != null ? Number(existingData.average_revenue) : undefined,
        profitMargin: existingData.profit_margin != null ? Number(existingData.profit_margin) : undefined,
        logo: existingData.logo || existingData.logoUrl || '',
        businessSectorId: existingData.businessSectorId || '',
        businessSectorName: existingData.businessSectorName || existingData.businessSector || '',
        businessTypeId: existingData.businessTypeId || '',
        businessTypeName: existingData.businessTypeName || existingData.businessType || '',
        businessType: existingData.businessType || existingData.businessTypeName || '',
        businessSector: existingData.businessSector || existingData.businessSectorName || '',
      });

      if (existingData.images?.length) {
        setImages(
          existingData.images.map((img: any) => ({
            id: img.imageId || img.id,
            url: img.image?.publicUrl || img.url || '',
            isPrimary: img.isPrimary,
          }))
        );
      }
    }
  }, [isEditMode, existingData, reset]);

  const countryId = watch('countryId') || 1;
  const provinceId = watch('provinceId');
  const districtId = watch('districtId');
  const wardId = watch('wardId');

  const { data: provinces } = useProvinces(countryId);
  const { data: districts } = useDistricts(provinceId);
  const { data: wards } = useWards(districtId);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newLocalImages: UploadedImg[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        addToast('error', `File ${file.name} không phải là hình ảnh hợp lệ.`);
        continue;
      }
      if (file.size > 1 * 1024 * 1024) {
        addToast('error', `Ảnh ${file.name} vượt quá dung lượng. Vui lòng chọn ảnh dưới 1MB.`);
        continue;
      }
      newLocalImages.push({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
        isPrimary: images.length === 0 && i === 0 && newLocalImages.length === 0,
        file,
      });
    }
    setImages((prev) => [...prev, ...newLocalImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      if (filtered.length > 0 && !filtered.find((img) => img.isPrimary)) {
        filtered[0].isPrimary = true;
      }
      return filtered;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
  };

  const onSubmit = async (data: FormData) => {
    if (data.investmentRequiredMax !== undefined && data.investmentRequiredMin !== undefined && data.investmentRequiredMax < data.investmentRequiredMin) {
      addToast('error', 'Vốn đầu tư tối đa phải lớn hơn hoặc bằng vốn đầu tư tối thiểu');
      return;
    }

    try {
      setIsUploading(true);
      const finalImages = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res && res.data) {
            finalImages.push({ id: res.data.id, type: img.isPrimary ? 'MAIN' : 'DETAIL', isPrimary: img.isPrimary });
          }
        } else {
          finalImages.push({ id: img.id, type: img.isPrimary ? 'MAIN' : 'DETAIL', isPrimary: img.isPrimary });
        }
      }

      let latitude = selectedGeo?.lat;
      let longitude = selectedGeo?.lon;

      if (!latitude || !longitude) {
        try {
          const provinceName = provinces?.find((p) => p.id === data.provinceId)?.name || '';
          const districtName = districts?.find((d) => d.id === data.districtId)?.name || '';
          const wardName = wards?.find((w) => w.id === data.wardId)?.name || '';

          const fullAddress = [data.headquarters, wardName, districtName, provinceName, 'Vietnam']
            .filter(Boolean)
            .join(', ');

          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`, {
            headers: {
              'Accept-Language': 'vi-VN,vi;q=0.9,en-US;q=0.8,en;q=0.7',
            }
          });
          const geoData = await res.json();

          if (geoData && geoData.length > 0) {
            latitude = geoData[0].lat;
            longitude = geoData[0].lon;
          }
        } catch (err) {
          console.error('Failed to geocode address', err);
        }
      }

      const payload = {
        name: data.name,
        description: data.description,
        logo: data.logo || undefined,
        countryId: data.countryId,
        provinceId: data.provinceId,
        districtId: data.districtId,
        wardId: data.wardId,
        latitude: latitude ? Number(latitude) : undefined,
        longitude: longitude ? Number(longitude) : undefined,
        areaUnit: data.areaUnit,
        currencyUnit: data.currencyUnit,
        website: data.website || undefined,
        contact_email: data.contactEmail || undefined,
        contact_phone: data.contactPhone || undefined,
        headquarters: data.headquarters,
        total_locations: data.totalLocations ? Number(data.totalLocations) : undefined,
        established_year: data.establishedYear ? Number(data.establishedYear) : undefined,
        investment_required_min: data.investmentRequiredMin ? Number(data.investmentRequiredMin) : undefined,
        investment_required_max: data.investmentRequiredMax ? Number(data.investmentRequiredMax) : undefined,
        royalty_fee: data.royaltyFee ? Number(data.royaltyFee) : undefined,
        franchise_fee: data.franchiseFee ? Number(data.franchiseFee) : undefined,
        advertising_fee: data.advertisingFee ? Number(data.advertisingFee) : undefined,
        contract_duration: data.contractDuration ? Number(data.contractDuration) : undefined,
        training_program: Boolean(data.trainingProgram),
        support_provided: data.supportProvided || undefined,
        requirements: data.requirements || undefined,
        target_market: data.targetMarket || undefined,
        average_revenue: data.averageRevenue ? Number(data.averageRevenue) : undefined,
        profit_margin: data.profitMargin ? Number(data.profitMargin) : undefined,
        businessSectorId: data.businessSectorId || undefined,
        businessSectorName: data.businessSectorName || data.businessSector || undefined,
        businessTypeId: data.businessTypeId || undefined,
        businessTypeName: data.businessTypeName || data.businessType || undefined,
        businessType: data.businessTypeName || data.businessType || undefined,
        businessSector: data.businessSectorName || data.businessSector || undefined,
        approvalStatus: 'pending',
        images: finalImages.map((img) => ({
          id: img.id,
          type: img.isPrimary ? 'banner' : 'detail',
          isPrimary: img.isPrimary,
        })),
      };

      if (isEditMode && id) {
        update(
          { id, data: payload as any },
          {
            onSuccess: () => navigate(`/nhuong-quyen/${id}`),
          }
        );
      } else {
        create(payload as any, {
          onSuccess: () => navigate(`/nhuong-quyen`),
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 413) {
        addToast('error', 'Ảnh tải lên quá lớn (vượt giới hạn 1MB). Vui lòng chọn ảnh nhỏ hơn.');
      } else {
        addToast('error', 'Lỗi khi tải ảnh lên. Vui lòng thử lại.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onInvalid = () => {
    addToast('error', 'Vui lòng kiểm tra lại các thông tin chưa hợp lệ.');
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-10">
      <div className="flex items-center gap-3 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl" style={{ color: 'var(--text-1)' }}>
            {isEditMode ? 'Cập nhật tin nhượng quyền' : 'Đăng tin nhượng quyền'}
          </h1>
          <p className="text-base mt-1" style={{ color: 'var(--text-3)' }}>
            {isEditMode ? 'Cập nhật thông tin thương hiệu của bạn' : 'Kết nối thương hiệu của bạn với đối tác tiềm năng'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        <FormCard title="Thông tin thương hiệu" icon={<Building2 size={18} />}>
          <div className="space-y-4">
            <Input label="Tên thương hiệu *" placeholder="VD: Phở 24, Highlands Coffee" error={errors.name?.message} {...register('name')} />
            <Textarea label="Mô tả" rows={4} placeholder="Giới thiệu thương hiệu, lịch sử, điểm nổi bật..." error={errors.description?.message} {...register('description')} />

            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Logo URL" placeholder="https://..." error={errors.logo?.message} {...register('logo')} />
              <Input label="Website" placeholder="https://..." error={errors.website?.message} {...register('website')} />
            </div>

            <BusinessSelector
              sectorId={watch('businessSectorId')}
              typeId={watch('businessTypeId')}
              onSectorChange={(id, name) => {
                setValue('businessSectorId', id);
                setValue('businessSectorName', name);
                setValue('businessSector', name);
                setValue('businessTypeId', '');
                setValue('businessTypeName', '');
                setValue('businessType', '');
              }}
              onTypeChange={(id, name) => {
                setValue('businessTypeId', id);
                setValue('businessTypeName', name);
                setValue('businessType', name);
              }}
              errors={{
                sectorId: errors.businessSectorId?.message,
                typeId: errors.businessTypeId?.message,
              }}
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <Controller
                name="establishedYear"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Năm thành lập"
                    options={YEAR_OPTIONS}
                    placeholder="-- Chọn năm --"
                    value={field.value ? String(field.value) : ''}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    error={errors.establishedYear?.message}
                  />
                )}
              />
              <Controller
                name="totalLocations"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Tổng số chi nhánh" placeholder="VD: 15" value={field.value} onChange={field.onChange} />
                )}
              />
            </div>
          </div>
        </FormCard>

        <FormCard title="Địa điểm & Trụ sở" icon={<MapPin size={18} />}>
          <div className="space-y-4">
            <LocationPicker
              addressField="headquarters"
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              selectedGeo={selectedGeo}
              onSelectGeo={setSelectedGeo}
            />


          </div>
        </FormCard>

        <FormCard title="Tài chính" icon={<DollarSign size={18} />}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Controller name="investmentRequiredMin" control={control} render={({ field }) => (
              <NumberInput label="Vốn đầu tư tối thiểu" placeholder="VD: 500,000,000" value={field.value} onChange={field.onChange} error={errors.investmentRequiredMin?.message} />
            )} />
            <Controller name="investmentRequiredMax" control={control} render={({ field }) => (
              <NumberInput label="Vốn đầu tư tối đa" placeholder="VD: 1,500,000,000" value={field.value} onChange={field.onChange} error={errors.investmentRequiredMax?.message} />
            )} />
            <Controller name="franchiseFee" control={control} render={({ field }) => (
              <NumberInput label="Phí nhượng quyền" placeholder="VD: 200,000,000" value={field.value} onChange={field.onChange} />
            )} />
            <Controller name="royaltyFee" control={control} render={({ field }) => (
              <NumberInput label="Phí bản quyền (%)" placeholder="VD: 5" value={field.value} onChange={field.onChange} />
            )} />
            <Controller name="advertisingFee" control={control} render={({ field }) => (
              <NumberInput label="Phí marketing (%)" placeholder="VD: 2" value={field.value} onChange={field.onChange} />
            )} />
            <Controller name="contractDuration" control={control} render={({ field }) => (
              <NumberInput label="Thời hạn hợp đồng (năm)" placeholder="VD: 5" value={field.value} onChange={field.onChange} />
            )} />
            <Controller name="averageRevenue" control={control} render={({ field }) => (
              <NumberInput label="Doanh thu trung bình/tháng" placeholder="VD: 300,000,000" value={field.value} onChange={field.onChange} />
            )} />
            <Controller name="profitMargin" control={control} render={({ field }) => (
              <NumberInput label="Biên lợi nhuận (%)" placeholder="VD: 25" value={field.value} onChange={field.onChange} />
            )} />
            <Controller
              name="currencyUnit"
              control={control}
              render={({ field }) => <Select label="Đơn vị tiền tệ" options={CURRENCIES} {...field} />}
            />

            <Controller
              name="areaUnit"
              control={control}
              render={({ field }) => <Select label="Đơn vị diện tích" options={AREA_UNITS} {...field} />}
            />

          </div>
        </FormCard>

        <FormCard title="Hỗ trợ & Yêu cầu" icon={<Layers size={18} />}>
          <div className="space-y-4">
            <Controller
              name="trainingProgram"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={<Checkbox checked={!!value} onChange={onChange} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
                  label={<span className="text-sm font-medium text-gray-800">Có chương trình đào tạo</span>}
                />
              )}
            />
            <Textarea label="Nội dung hỗ trợ" rows={3} placeholder="Đào tạo nhân sự, marketing..." {...register('supportProvided')} />
            <Textarea label="Yêu cầu mặt bằng/đối tác" rows={3} placeholder="Mặt bằng tối thiểu 80m²..." {...register('requirements')} />
            <Input label="Thị trường mục tiêu" placeholder="VD: Các thành phố lớn..." {...register('targetMarket')} />
          </div>
        </FormCard>

        <FormCard title="Liên hệ" icon={<Phone size={18} />}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Số điện thoại *" placeholder="0987654321" error={errors.contactPhone?.message} {...register('contactPhone')} />
            <Input label="Email *" type="email" placeholder="contact@brand.com" error={errors.contactEmail?.message} {...register('contactEmail')} />
          </div>
        </FormCard>

        <FormCard title="Hình ảnh nhượng quyền" icon={<LinkIcon size={18} />}>
          <div className="space-y-4">
            <FileUploadDropzone
              onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
              isUploading={isUploading}
              multiple
              accept="image/*,.pdf"
              title="Nhấn để chọn hoặc kéo thả ảnh vào đây"
              subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
            />

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {images.map((img) => (
                  <div key={img.id} className={cn(
                    "relative aspect-video rounded-xl overflow-hidden border-2 group",
                    img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"
                  )}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Xóa ảnh"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {img.isPrimary ? (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                        Ảnh chính
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPrimaryImage(img.id)}
                        className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Đặt làm ảnh chính
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormCard>

        <div className="flex gap-4 justify-end pt-4 border-t" style={{ borderColor: 'var(--border-c)' }}>
          <Button type="button" variant="ghost" onClick={() => navigate(isEditMode ? `/nhuong-quyen/${id}` : '/nhuong-quyen')} className="px-8 font-bold">
            Hủy
          </Button>
          <Button type="submit" variant="brand" loading={isPending || isUploading} className="px-8 font-bold text-base shadow-lg shadow-orange-500/20">
            {isEditMode ? 'Lưu thay đổi' : 'Đăng tin'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function FormCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border p-6 md:p-8 bg-white shadow-sm" style={{ borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-lg flex items-center gap-2 pb-4 mb-4 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
          {icon}
        </div>
        {title}
      </h2>
      <div>
        {children}
      </div>
    </div>
  );
}
